import React from 'react';
import {ForecastQueryRenderer} from '../../../../ForecastQueryRenderer';
import {REPORT_GROUPINGS, HIDDEN_FEATURES} from '../../../../constants';
import ProjectRelayWrapper, {ProjectRelayWrapperV2Query} from './relay-wrappers/ProjectRelayWrapperV2';
import PhaseRelayWrapper, {PhaseRelayWrapperV2Query} from './relay-wrappers/PhaseRelayWrapperV2';
import ClientRelayWrapper, {ClientRelayWrapperQuery} from './relay-wrappers/ClientRelayWrapperV2';
import TaskReportRelayWrapper, {TaskRelayWrapperV2Query} from './relay-wrappers/TaskRelayWrapperV2';
import RoleRelayWrapper, {RoleRelayWrapperV2Query} from './relay-wrappers/RoleRelayWrapperV2';
import {parseTaskFilters} from '../../../shared/components/filters/filter_logic';
import SprintRelayWrapper, {SprintRelayWrapperV2Query} from './relay-wrappers/SprintRelayWrapperV2';
import {isFeatureHidden} from '../../../shared/util/CompanySetupUtil';
import {TaskTotalsHeaders as Totals} from './renders/TaskTotalsHeaders';
import {hasFeatureFlag} from '../../../shared/util/FeatureUtil';
import {useIntl} from 'react-intl';
import {TaskBreakdownAndReportHeadersTooltips as Tooltips} from '../../../shared/components/tooltips/TaskBreakdownAndReportHeadersTooltips';

export const getSecondDropdownOptionsByGroupings = (firstDropdownValue, intl) => {
	const secondDropdownOptions = [];
	switch (firstDropdownValue) {
		case REPORT_GROUPINGS.PHASE:
			secondDropdownOptions.push(
				{value: REPORT_GROUPINGS.PROJECT, label: intl.formatMessage({id: 'common.group_by_project'})},
				...(!isFeatureHidden(HIDDEN_FEATURES.CLIENT)
					? [{value: REPORT_GROUPINGS.CLIENT, label: intl.formatMessage({id: 'common.group_by_client'})}]
					: []),
				{value: REPORT_GROUPINGS.ROLE, label: intl.formatMessage({id: 'common.group_by_role'})},
				{value: null, label: 'No grouping'}
			);
			break;
		case REPORT_GROUPINGS.PROJECT:
			secondDropdownOptions.push(
				{value: REPORT_GROUPINGS.PHASE, label: intl.formatMessage({id: 'common.group_by_phase'})},
				{value: REPORT_GROUPINGS.ROLE, label: intl.formatMessage({id: 'common.group_by_role'})},
				{value: REPORT_GROUPINGS.SPRINT, label: intl.formatMessage({id: 'common.group_by_sprint'})},
				{value: null, label: 'No grouping'}
			);
			break;
		case REPORT_GROUPINGS.CLIENT:
			secondDropdownOptions.push(
				{value: REPORT_GROUPINGS.PROJECT, label: intl.formatMessage({id: 'common.group_by_project'})},
				{value: REPORT_GROUPINGS.ROLE, label: intl.formatMessage({id: 'common.group_by_role'})},
				{value: REPORT_GROUPINGS.PHASE, label: intl.formatMessage({id: 'common.group_by_phase'})},
				{value: REPORT_GROUPINGS.SPRINT, label: intl.formatMessage({id: 'common.group_by_sprint'})},
				{value: null, label: 'No grouping'}
			);
			break;
		case REPORT_GROUPINGS.ROLE:
			secondDropdownOptions.push(
				{value: REPORT_GROUPINGS.PROJECT, label: intl.formatMessage({id: 'common.group_by_project'})},
				...(!isFeatureHidden(HIDDEN_FEATURES.CLIENT)
					? [{value: REPORT_GROUPINGS.CLIENT, label: intl.formatMessage({id: 'common.group_by_client'})}]
					: []),
				{value: REPORT_GROUPINGS.PHASE, label: intl.formatMessage({id: 'common.group_by_phase'})},
				{value: REPORT_GROUPINGS.SPRINT, label: intl.formatMessage({id: 'common.group_by_sprint'})},
				{value: null, label: 'No grouping'}
			);
			break;
		case null:
			return null;
		default:
			// A grouping this page has no second level for. Null is what the page reads as "offer no
			// second dropdown"; a string here used to reach the dropdown as its options and crash it.
			return null;
	}
	return secondDropdownOptions;
};

const pageSize = 30;

/**
 * This will convert the selected task filters into Global Task filters
 * used in the search query filters.
 *
 * @param defaultSearchQueryFilters The default search query filters.
 * @param filters The task filters
 * @returns {*[]} An array of Global Task filters
 */
export const getSearchQueryFiltersValue = (defaultSearchQueryFilters, filters) => {
	const parsedFilters = parseTaskFilters(filters, false, false);
	return [...defaultSearchQueryFilters, ...parsedFilters];
};

const getRelayWrapper = (
	tableHeader,
	groupings,
	searchQuery,
	rowIndex,
	enabledColumns,
	relayProps,
	nextLevelProps,
	currency,
	customFieldColumnCount,
	customFieldKeys
) => {
	switch (groupings[0]) {
		case REPORT_GROUPINGS.PROJECT:
			return (
				<ProjectRelayWrapper
					{...relayProps}
					tableHeader={tableHeader}
					groupings={groupings.slice(1)}
					searchQuery={searchQuery}
					rowIndex={rowIndex + 1}
					enabledColumns={enabledColumns}
					nextLevelProps={nextLevelProps}
					currency={currency}
					customFieldColumnCount={customFieldColumnCount}
					customFieldKeys={customFieldKeys}
					pageSize={pageSize}
				/>
			);

		case REPORT_GROUPINGS.TASK:
			return (
				<TaskReportRelayWrapper
					{...relayProps}
					tableHeader={tableHeader}
					searchQuery={searchQuery}
					rowIndex={rowIndex + 1}
					enabledColumns={enabledColumns}
					nextLevelProps={nextLevelProps}
					currency={currency}
					customFieldKeys={customFieldKeys}
					pageSize={pageSize}
				/>
			);

		case REPORT_GROUPINGS.PHASE:
			return (
				<PhaseRelayWrapper
					{...relayProps}
					tableHeader={tableHeader}
					groupings={groupings.slice(1)}
					searchQuery={searchQuery}
					rowIndex={rowIndex + 1}
					enabledColumns={enabledColumns}
					nextLevelProps={nextLevelProps}
					currency={currency}
					customFieldColumnCount={customFieldColumnCount}
					customFieldKeys={customFieldKeys}
					pageSize={pageSize}
				/>
			);

		case REPORT_GROUPINGS.CLIENT:
			return (
				<ClientRelayWrapper
					{...relayProps}
					tableHeader={tableHeader}
					groupings={groupings.slice(1)}
					searchQuery={searchQuery}
					rowIndex={rowIndex + 1}
					enabledColumns={enabledColumns}
					nextLevelProps={nextLevelProps}
					currency={currency}
					customFieldColumnCount={customFieldColumnCount}
					customFieldKeys={customFieldKeys}
					pageSize={pageSize}
				/>
			);

		case REPORT_GROUPINGS.ROLE:
			return (
				<RoleRelayWrapper
					{...relayProps}
					tableHeader={tableHeader}
					groupings={groupings.slice(1)}
					searchQuery={searchQuery}
					rowIndex={rowIndex + 1}
					enabledColumns={enabledColumns}
					nextLevelProps={nextLevelProps}
					currency={currency}
					customFieldColumnCount={customFieldColumnCount}
					customFieldKeys={customFieldKeys}
					pageSize={pageSize}
				/>
			);

		case REPORT_GROUPINGS.SPRINT:
			return (
				<SprintRelayWrapper
					{...relayProps}
					tableHeader={tableHeader}
					groupings={groupings.slice(1)}
					searchQuery={searchQuery}
					rowIndex={rowIndex + 1}
					enabledColumns={enabledColumns}
					nextLevelProps={nextLevelProps}
					currency={currency}
					customFieldColumnCount={customFieldColumnCount}
					customFieldKeys={customFieldKeys}
					pageSize={pageSize}
				/>
			);

		default:
			// Paired with getQuery/getComponentName's fallbacks: an unknown grouping falls back to the
			// flat task list, so the wrapper rendered matches the query actually run rather than
			// dropping a placeholder string into the table body.
			return (
				<TaskReportRelayWrapper
					{...relayProps}
					tableHeader={tableHeader}
					searchQuery={searchQuery}
					rowIndex={rowIndex + 1}
					enabledColumns={enabledColumns}
					nextLevelProps={nextLevelProps}
					currency={currency}
					customFieldKeys={customFieldKeys}
					pageSize={pageSize}
				/>
			);
	}
};

const getComponentName = groupings => {
	switch (groupings[0]) {
		case REPORT_GROUPINGS.PROJECT:
			return 'project_relay_wrapper';

		case REPORT_GROUPINGS.TASK:
			return 'task_relay_wrapper';

		case REPORT_GROUPINGS.ROLE:
			return 'role_relay_wrapper';

		case REPORT_GROUPINGS.PHASE:
			return 'phase_relay_wrapper';

		case REPORT_GROUPINGS.CLIENT:
			return 'client_relay_wrapper';

		case REPORT_GROUPINGS.SPRINT:
			return 'sprint_relay_wrapper';

		default:
			// Paired with getQuery's fallback so the name always describes the query actually run.
			return 'task_relay_wrapper';
	}
};

const getQuery = groupings => {
	switch (groupings[0]) {
		case REPORT_GROUPINGS.PROJECT:
			return ProjectRelayWrapperV2Query;

		case REPORT_GROUPINGS.TASK:
			return TaskRelayWrapperV2Query;

		case REPORT_GROUPINGS.CLIENT:
			return ClientRelayWrapperQuery;

		case REPORT_GROUPINGS.ROLE:
			return RoleRelayWrapperV2Query;

		case REPORT_GROUPINGS.PHASE:
			return PhaseRelayWrapperV2Query;

		case REPORT_GROUPINGS.SPRINT:
			return SprintRelayWrapperV2Query;

		default:
			// The value returned here is handed straight to Relay, which throws during render on anything
			// that is not a compiled query. Groupings are normalised before they reach this switch, so
			// the fallback is the flat task list rather than a placeholder that would take the page down.
			return TaskRelayWrapperV2Query;
	}
};

const keyReducer = (accumulator, currentValue) =>
	accumulator + (currentValue && currentValue.length > 0 ? currentValue.value[0] : '');

const getQueryInfo = (groupings, searchQuery, object) => {
	const queryInfo = {};
	const keyEnding = searchQuery.filters.length > 0 && searchQuery.filters.reduce(keyReducer);
	const keyEnding2 = object && object.field + object.value;

	queryInfo.key = `query-render-relayWrapper-${keyEnding}-${keyEnding2}`;
	queryInfo.componentName = getComponentName(groupings);
	queryInfo.query = getQuery(groupings);
	return queryInfo;
};

export const getQueryRenderer = (
	tableHeader,
	groupings,
	searchQuery,
	rowIndex,
	enabledColumns,
	groupingFilter,
	nextLevelProps,
	currency,
	reportId,
	sharedId,
	customFieldColumnCount,
	customFieldKeys
) => {
	let searchQueryFiltersCopy = [...searchQuery.filters];
	if (groupingFilter !== null) {
		searchQueryFiltersCopy = searchQueryFiltersCopy.filter(filter => filter.field !== groupingFilter.field);
		searchQueryFiltersCopy.push(groupingFilter);
	}
	const newSearchQuery = {filters: searchQueryFiltersCopy};
	const query = getQueryInfo(groupings, searchQuery, groupingFilter);
	return (
		<ForecastQueryRenderer
			key={query.key}
			query={query.query}
			renderIfNotLoggedIn={true}
			variables={{
				pageSize,
				searchQuery: newSearchQuery,
				reportId,
				sharedId,
				customFieldKeys: customFieldKeys,
			}}
			render={relayProps => {
				return getRelayWrapper(
					tableHeader,
					groupings,
					newSearchQuery,
					rowIndex,
					enabledColumns,
					relayProps,
					nextLevelProps,
					currency,
					customFieldColumnCount,
					customFieldKeys
				);
			}}
		/>
	);
};

/**
 *  Return the options for the first grouping.
 *
 * @param intl The I18n object
 * @returns Array of available groupings
 */
export const noGroupingFirstDropdownOptions = () => {
	const {formatMessage} = useIntl();
	return [
		{value: REPORT_GROUPINGS.PROJECT, label: formatMessage({id: 'common.group_by_project'})},
		{value: REPORT_GROUPINGS.PHASE, label: formatMessage({id: 'common.group_by_phase'})},
		...(!isFeatureHidden(HIDDEN_FEATURES.CLIENT)
			? [{value: REPORT_GROUPINGS.CLIENT, label: formatMessage({id: 'common.group_by_client'})}]
			: []),
		{value: REPORT_GROUPINGS.ROLE, label: formatMessage({id: 'common.group_by_role'})},
		{value: null, label: formatMessage({id: 'project_sprints.group_by_people_disable'})},
	];
};

export const getTotalTimeEntries = timeRegistrations => {
	if (timeRegistrations !== undefined) {
		return timeRegistrations.edges.reduce((acc, elem) => acc + elem.node.minutesRegistered, 0);
	}
	return 0;
};

export const useTaskReportColumns = customFieldDefinitions => {
	const hasFinancialCategoriesUpdate = hasFeatureFlag('financial_categories_update');
	const hasDeliverables = hasFeatureFlag('deliverables');
	const {formatMessage} = useIntl();

	const headerList = [
		{
			id: 'name',
			extraProps: {width: 'LARGE', flex: 1},
			Total: ({row}) => <span>{row?.generatedName || row?.name}</span>,
		},
		{id: 'indicator', title: formatMessage({id: 'common.indicators'}), eyeOptionId: 'task.indicators'},
		{
			id: 'project',
			title: formatMessage({id: 'common.project'}),
			extraProps: {width: 'LARGE'},
			eyeOptionId: 'task.project.info',
		},
		{
			id: 'project_deadline',
			title: formatMessage({id: 'insights.component.list.column.projectDeadline'}),
			eyeOptionId: 'task.project.deadline',
		},
		{
			id: 'project_stage',
			title: formatMessage({id: 'insights.component.list.column.projectStage'}),
			eyeOptionId: 'task.project.stage',
		},
		{id: 'rateCard', title: formatMessage({id: 'common.rate-card'}), eyeOptionId: 'task.project.rateCard.name'},
		{
			id: 'client',
			title: formatMessage({id: 'common.client'}),
			extraProps: {width: 'SMALL'},
			eyeOptionId: 'task.project.client.info',
		},
		{
			id: 'task_startDate',
			title: formatMessage({id: 'common.task'}) + ' ' + formatMessage({id: 'common.start_date'}),
			eyeOptionId: 'task.dates.start',
		},
		{
			id: 'task_endDate',
			title: formatMessage({id: 'common.task'}) + ' ' + formatMessage({id: 'common.end_date'}),
			eyeOptionId: 'task.dates.deadline',
		},
		{
			id: 'task_createdAt',
			title: formatMessage({id: 'common.task'}) + ' ' + formatMessage({id: 'insights.component.list.column.createdAt'}),
			eyeOptionId: 'task.dates.createdAt',
		},
		{id: 'role', title: formatMessage({id: 'common.role'}), eyeOptionId: 'task.role.info'},
		{
			id: 'assignees',
			title: formatMessage({id: 'common.assignees'}),
			extraProps: {width: 'LARGE'},
			eyeOptionId: 'task.assignees',
		},
		{id: 'status', title: formatMessage({id: 'common.status'}), eyeOptionId: 'task.status.name'},
		{id: 'phase', title: formatMessage({id: 'common.phase'}), eyeOptionId: 'task.phase.info'},
		{
			id: 'phase_startDate',
			title: formatMessage({id: 'common.phase'}) + ' ' + formatMessage({id: 'common.start_date'}),
			eyeOptionId: 'task.phase.dates.start',
		},
		{
			id: 'phase_endDate',
			title: formatMessage({id: 'common.phase'}) + ' ' + formatMessage({id: 'common.end_date'}),
			eyeOptionId: 'task.phase.dates.deadline',
		},
		{
			id: 'deliverable',
			title: formatMessage({id: 'common.deliverable'}),
			eyeOptionId: 'task.deliverable.info',
			showCondition: hasDeliverables,
		},
		{id: 'sprint', title: formatMessage({id: 'common.sprint'}), eyeOptionId: 'task.sprint.info'},
		{
			id: 'sprint_startDate',
			title: formatMessage({id: 'common.sprint'}) + ' ' + formatMessage({id: 'common.start_date'}),
			eyeOptionId: 'task.sprint.dates.start',
		},
		{
			id: 'sprint_endDate',
			title: formatMessage({id: 'common.sprint'}) + ' ' + formatMessage({id: 'common.end_date'}),
			eyeOptionId: 'task.sprint.dates.deadline',
		},
		{
			id: 'progress',
			title: formatMessage({id: 'common.progress'}),
			extraProps: {align: 'right', width: 'SMALL'},
			eyeOptionId: 'task.progress',
		},
		{
			id: 'estimate_hours',
			title: formatMessage({id: 'common.estimate'}) + ' ' + formatMessage({id: 'common.hours'}),
			extraProps: {align: 'right'},
			Total: ({row}) => <Totals.EstimateHours totals={row.taskTotals} />,
			eyeOptionId: 'task.work.estimate.minutes',
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'estimate_points',
			title: formatMessage({id: 'common.estimate'}) + ' ' + formatMessage({id: 'common.points'}),
			extraProps: {align: 'right'},
			Total: ({row}) => <Totals.EstimatePoints totals={row.taskTotals} />,
			eyeOptionId: 'task.work.estimate.points',
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'projected',
			title: formatMessage({id: 'common.projected'}),
			extraProps: {align: 'right'},
			Total: ({row}) => <Totals.ProjectedHours totals={row.taskTotals} />,
			eyeOptionId: 'task.reported.projected',
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'reported',
			title: formatMessage({id: 'common.reported'}),
			extraProps: {align: 'right'},
			Total: ({row}) => <Totals.ReportedHours totals={row.taskTotals} />,
			eyeOptionId: 'task.reported.time',
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'remaining_hours',
			title: formatMessage({id: 'common.remaining'}) + ' ' + formatMessage({id: 'common.hours'}),
			extraProps: {align: 'right'},
			Total: ({row}) => <Totals.RemainingHours totals={row.taskTotals} />,
			eyeOptionId: 'task.work.remaining.minutes',
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'remaining_points',
			title: formatMessage({id: 'common.remaining'}) + ' ' + formatMessage({id: 'common.points'}),
			extraProps: {align: 'right'},
			Total: ({row}) => <Totals.RemainingPoints totals={row.taskTotals} />,
			eyeOptionId: 'task.work.remaining.points',
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'difference_hours',
			title: formatMessage({id: 'common.difference'}) + ' ' + formatMessage({id: 'common.hours'}),
			extraProps: {align: 'right'},
			Total: ({row}) => <Totals.DifferenceHours totals={row.taskTotals} />,
			eyeOptionId: 'task.work.difference.minutes',
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'difference_points',
			title: formatMessage({id: 'common.difference'}) + ' ' + formatMessage({id: 'common.points'}),
			extraProps: {align: 'right'},
			Total: ({row}) => <Totals.DifferencePoints totals={row.taskTotals} />,
			eyeOptionId: 'task.work.difference.points',
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'planned_revenue',
			title: formatMessage({id: 'project_budget.planned_billable_time'}),
			extraProps: {align: 'right'},
			Total: ({row}) => <Totals.PlannedRevenue totals={row.taskTotals} currency={row.currency} />,
			eyeOptionId: 'task.financial.revenue.planned',
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'actual_revenue',
			title: formatMessage({id: 'project_budget.actual_billable_time'}),
			extraProps: {align: 'right'},
			Total: ({row}) => <Totals.ActualRevenue totals={row.taskTotals} currency={row.currency} />,
			eyeOptionId: 'task.financial.revenue.actual',
			showCondition: !hasFinancialCategoriesUpdate,
		},
		{
			id: 'hours-estimated',
			title: formatMessage({id: 'common.estimated_hours'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.hours.estimated',
			Total: ({row}) => <Totals.EstimateHours totals={row.taskTotals} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.EstimatedHoursTooltip {...props} />,
		},
		{
			id: 'hours-actual',
			title: formatMessage({id: 'common.actual_hours'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.hours.actual',
			Total: ({row}) => <Totals.ReportedHours totals={row.taskTotals} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.ActualHoursTooltip {...props} />,
		},
		{
			id: 'hours-actual-billable',
			title: formatMessage({id: 'common.actual_billable_hours'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.hours.actual_billable',
			Total: ({row}) => <Totals.BillableActualHours totals={row.taskTotals} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.ActualBillableHoursTooltip {...props} />,
		},
		{
			id: 'hours-actual-non-billable',
			title: formatMessage({id: 'common.actual_non_billable_hours'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.hours.actual_non_billable',
			Total: ({row}) => <Totals.NonBillableActualHours totals={row.taskTotals} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.ActualNonBillableHoursTooltip {...props} />,
		},
		{
			id: 'remaining-work-hours',
			title: formatMessage({id: 'common.remaining_work_hours'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.hours.remaining',
			Total: ({row}) => <Totals.RemainingHours totals={row.taskTotals} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.RemainingWorkHoursTooltip {...props} />,
		},
		{
			id: 'projected-total-hours',
			title: formatMessage({id: 'common.projected_total_hours'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.hours.projected',
			Total: ({row}) => <Totals.ProjectedHours totals={row.taskTotals} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.ProjectedTotalHoursTooltip {...props} size="l" />,
		},
		{
			id: 'estimated-points',
			title: formatMessage({id: 'common.estimated_points'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.points.estimated_points',
			Total: ({row}) => <Totals.EstimatePoints totals={row.taskTotals} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.EstimatedPointsTooltip {...props} />,
		},
		{
			id: 'remaining-work-points',
			title: formatMessage({id: 'common.remaining_work_points'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.points.remaining_work_points',
			Total: ({row}) => <Totals.RemainingPoints totals={row.taskTotals} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.RemainingWorkPointsTooltip {...props} />,
		},
		{
			id: 'estimated-billable-value-of-service',
			title: formatMessage({id: 'common.estimated_billable_value_of_service'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.financials.estimated_billable_value_of_service',
			Total: ({row}) => <Totals.BillablePlannedTimeAndExpenses totals={row.taskTotals} currency={row.currency} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.EstimatedBillableValueOfServiceTooltip {...props} />,
		},
		{
			id: 'actual-billable-value-of-service',
			title: formatMessage({id: 'common.actual_billable_value_of_service'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.financials.actual_billable_value_of_service',
			Total: ({row}) => <Totals.BillableActualTimeAndExpenses totals={row.taskTotals} currency={row.currency} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.ActualBillableValueOfServiceTooltip {...props} />,
		},
		{
			id: 'remaining-work-billable',
			title: formatMessage({id: 'common.remaining_work_billable_value_of_service'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.financials.remaining_work_billable_value_of_service',
			Total: ({row}) => (
				<Totals.BillableForecastTimeAndExpensesToComplete totals={row.taskTotals} currency={row.currency} />
			),
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.RemainingWorkBillableValueOfServiceTooltip {...props} size="l" />,
		},
		{
			id: 'projected-total-billable',
			title: formatMessage({id: 'common.projected_total_billable_value_of_service'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.financials.projected_total_billable_value_of_service',
			Total: ({row}) => (
				<Totals.BillableTotalTimeAndExpensesAtCompletion totals={row.taskTotals} currency={row.currency} />
			),
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.ProjectedTotalBillableValueOfServiceTooltip {...props} size="xl" />,
		},
		{
			id: 'estimated-revenue',
			title: formatMessage({id: 'common.estimated_revenue'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.financials.estimated_revenue',
			Total: ({row}) => <Totals.PlannedRevenue totals={row.taskTotals} currency={row.currency} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.EstimatedRevenueTooltip {...props} />,
		},
		{
			id: 'actual-revenue',
			title: formatMessage({id: 'common.actual_revenue'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.financials.actual_revenue',
			Total: ({row}) => <Totals.ActualRevenue totals={row.taskTotals} currency={row.currency} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.ActualRevenueTooltip {...props} size="xl" />,
		},
		{
			id: 'remaining-work-revenue',
			title: formatMessage({id: 'common.remaining_work_revenue'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.financials.remaining_work_revenue',
			Total: ({row}) => <Totals.TotalForecastRevenueToComplete totals={row.taskTotals} currency={row.currency} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.RemainingWorkRevenueTooltip {...props} size="xl" />,
		},
		{
			id: 'projected-total-revenue',
			title: formatMessage({id: 'common.projected_total_revenue'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.financials.projected_total_revenue',
			Total: ({row}) => <Totals.TotalRevenueRecognition totals={row.taskTotals} currency={row.currency} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.ProjectedTotalRevenueTooltip {...props} size="l" />,
		},
		{
			id: 'projected-over-under',
			title: formatMessage({id: 'common.projected_over_under_servicing'}),
			extraProps: {align: 'right'},
			eyeOptionId: 'task.financials.projected_over_under_servicing',
			Total: ({row}) => <Totals.Surplus totals={row.taskTotals} currency={row.currency} />,
			showCondition: hasFinancialCategoriesUpdate,
			Tooltip: props => <Tooltips.ProjectedOverUnderServicingTooltip {...props} size="xl" />,
		},
		{
			id: 'approved',
			title: formatMessage({id: 'common.approved'}),
			extraProps: {align: 'center', width: 'SMALL'},
			eyeOptionId: 'task.approved',
		},
		{
			id: 'labels',
			title: formatMessage({id: 'common.labels'}),
			extraProps: {width: 'EXTRA_LARGE'},
			eyeOptionId: 'task.labels',
		},
		...customFieldDefinitions.map(customField => ({
			id: customField.name,
			title: customField.displayName,
			eyeOptionId: 'company.reportCustomFieldDefinitions.' + customField.key,
			extraProps: {align: 'center'},
		})),
	];

	return headerList.filter(item => item.showCondition ?? true);
};
